import { PricingPeriod } from '@atlassiansox/bxp-use-pricing';
import { createHook, createStore } from 'react-sweet-state';
import {
  GlobalState,
  ProductFamilyName,
  ProductName,
  SelectedAppDetails,
  SelectedProductDetails,
  marketplaceAddonDetails
} from './models';

export const initialState: GlobalState = {
  currentStep: 0,
  scrollToAddedProduct: false,

  selectedBillingCycle: 'MONTHLY',

  selectedProductFamilyNames: new Set(),
  selectedProductNames: new Set(),
  selectedMarketplaceAddons: new Map(),

  unitCountByProduct: new Map(),
  priceTotalByProductFamily: {},

  freePlanProducts: new Set(),
  selectedProductsWithDetails: new Map(),
  selectedAppsWithDetails: new Map(),

  appErrors: new Map()
};

export const store = createStore({
  name: 'global-store',
  initialState,
  actions: {
    toggleProductSelection:
      (productName: ProductName, doScroll?: boolean) =>
      ({ setState, getState }) => {
        const {
          selectedProductNames: currentSelectedProductNames,
          unitCountByProduct
        } = getState();
        const selectedProductNames = new Set(currentSelectedProductNames);
        const currentUnitCountByProduct = new Map(unitCountByProduct);
        if (selectedProductNames.has(productName)) {
          selectedProductNames.delete(productName);
          if (currentUnitCountByProduct.has(productName)) {
            currentUnitCountByProduct.delete(productName);
          }
        } else {
          selectedProductNames.add(productName);
        }

        setState({
          selectedProductNames,
          scrollToAddedProduct: doScroll,
          unitCountByProduct: currentUnitCountByProduct
        });
      },

    selectStep:
      (selectedStep: 0 | 1) =>
      ({ setState }) => {
        setState({
          currentStep: selectedStep
        });
      },

    selectBillingCycle:
      (selectedBillingCycle: PricingPeriod) =>
      ({ setState }) => {
        setState({
          selectedBillingCycle
        });
      },

    incrementTotal:
      (productFamilyName: ProductFamilyName, total: number) =>
      ({ setState, getState }) => {
        const { priceTotalByProductFamily: currentPriceTotalByProductFamily } =
          getState();

        const priceTotalByProductFamily = Object.assign(
          {},
          currentPriceTotalByProductFamily
        );

        const currentTotal = priceTotalByProductFamily[productFamilyName];
        if (currentTotal !== undefined) {
          priceTotalByProductFamily[productFamilyName] = currentTotal + total;
        } else {
          priceTotalByProductFamily[productFamilyName] = total;
        }

        setState({
          priceTotalByProductFamily
        });
      },

    setUnitCountByProduct:
      (productName: ProductName, unitCount: number) =>
      ({ setState, getState }) => {
        const { unitCountByProduct } = getState();

        const currentUnitCountByProduct = new Map(unitCountByProduct);
        currentUnitCountByProduct.set(productName, unitCount);
        setState({
          unitCountByProduct: currentUnitCountByProduct
        });
      },

    setFreePlanProduct:
      (productFreePlanSelected: ProductName) =>
      ({ setState, getState }) => {
        const { freePlanProducts: currentFreePlanProducts } = getState();
        const freePlanProducts = new Set(currentFreePlanProducts);

        if (!freePlanProducts.has(productFreePlanSelected)) {
          freePlanProducts.add(productFreePlanSelected);
        }

        setState({
          freePlanProducts
        });
      },

    removeFreePlanProduct:
      (productFreePlanSelected: ProductName) =>
      ({ setState, getState }) => {
        const { freePlanProducts: currentFreePlanProducts } = getState();
        const freePlanProducts = new Set(currentFreePlanProducts);

        if (freePlanProducts.has(productFreePlanSelected)) {
          freePlanProducts.delete(productFreePlanSelected);
        }

        setState({
          freePlanProducts
        });
      },

    updateMarketplaceApp:
      (
        productName: ProductFamilyName,
        marketplaceApp: marketplaceAddonDetails
      ) =>
      ({ setState, getState }) => {
        const { selectedMarketplaceAddons, appErrors } = getState();
        const currentAddons = new Map(selectedMarketplaceAddons);
        const currentAppErrors = new Map(appErrors);

        if (currentAddons.has(productName)) {
          if (currentAddons.get(productName)?.has(marketplaceApp)) {
            currentAddons.get(productName)?.delete(marketplaceApp);
          } else {
            currentAddons.get(productName)?.add(marketplaceApp);
            currentAppErrors.delete(marketplaceApp.name);
          }
        } else {
          currentAddons.set(productName, new Set());
          currentAddons.get(productName)?.add(marketplaceApp);
        }
        setState({
          selectedMarketplaceAddons: currentAddons,
          appErrors: currentAppErrors
        });
      },

    restart:
      () =>
      ({ setState }) => {
        setState({ ...initialState });
      },

    updateSelectedProductsWithDetails:
      (selectedProductsWithDetails: Map<ProductName, SelectedProductDetails>) =>
      ({ setState }) => {
        setState({
          selectedProductsWithDetails
        });
      },

    updateSelectedAppsWithDetails:
      (selectedAppsWithDetails: Map<string, SelectedAppDetails>) =>
      ({ setState }) => {
        setState({
          selectedAppsWithDetails
        });
      },

    appendAppError:
      (appName: string, message: string) =>
      ({ setState, getState }) => {
        const appErrors = new Map(getState().appErrors);
        appErrors.set(appName, message);
        setState({ appErrors });
      },

    removeAppError:
      (appName: string) =>
      ({ setState, getState }) => {
        const appErrors = new Map(getState().appErrors);
        appErrors.delete(appName);
        setState({ appErrors });
      }
  }
});
export const useGlobalStore = createHook(store);
